/**
 * AdminNFTEconomy.js — Command Centre: the teen NFT economy in one pane.
 * Mint queue, live listings, sales, teen wallet ledger + one-click Mark Sold.
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Gem, Loader2, RefreshCw, Hammer, Tag, PoundSterling, Wallet, Check, Copy } from 'lucide-react';
import { apiClient } from '../../api/client';

const Tile = ({ icon: Icon, color, label, value, sub, testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={16} style={{ color }} />
      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-400">{label}</p>
    </div>
    <p className="text-2xl font-black text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const STATUS_CHIP = {
  listed: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  sold: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function AdminNFTEconomy() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await apiClient.request('/api/admin/nft-economy')); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markSold = async (listing) => {
    const suggested = listing.price_pence ? (listing.price_pence / 100).toFixed(2) : '';
    const input = window.prompt(
      `Confirm sale amount in £ for "${listing.title}" — this credits the teen's wallet and cannot be undone:`,
      suggested
    );
    if (input === null) return;
    const pence = Math.round(parseFloat(input) * 100);
    if (!pence || pence <= 0) { alert('Enter a valid amount.'); return; }
    setSettling(listing.listing_id);
    try {
      await apiClient.request(`/api/nft/listings/${listing.listing_id}/mark-sold`, {
        method: 'POST',
        body: JSON.stringify({ amount_pence: pence }),
      });
      await load();
    } catch (e) { alert(e?.message || 'Settlement failed'); }
    setSettling(null);
  };

  const copyLink = async (l) => {
    const url = `${window.location.origin}/nft-listing/${l.listing_id}`;
    try { await navigator.clipboard.writeText(url); alert('Share link copied'); }
    catch { window.prompt('Copy link:', url); }
  };

  const s = data?.summary || {};
  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl" data-testid="admin-nft-economy-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <Gem className="text-amber-500" size={24} /> NFT Economy
            </h1>
            <p className="text-sm text-gray-500">Mint queue → listings → sales → teen wallets. Proceeds always settle to the teen.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700" data-testid="nft-economy-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-500" size={30} /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              <Tile icon={Hammer} color="#F59E0B" label="Mint Queue" value={s.mint_queue_count ?? 0} sub="teen-ticked, awaiting parent" testid="tile-mint-queue" />
              <Tile icon={Loader2} color="#6366F1" label="Confirming" value={s.pending_mint_count ?? 0} sub="on-chain, auto-checked 5min" testid="tile-pending-mints" />
              <Tile icon={Tag} color="#0EA5E9" label="Live Listings" value={s.live_listing_count ?? 0} sub="for sale right now" testid="tile-live-listings" />
              <Tile icon={PoundSterling} color="#10B981" label="Sales" value={s.sold_count ?? 0} sub={`£${((s.sold_total_pence || 0) / 100).toFixed(2)} total`} testid="tile-sales" />
              <Tile icon={Wallet} color="#8B5CF6" label="Teen Wallets" value={`£${((s.wallet_total_pence || 0) / 100).toFixed(2)}`} sub={`across ${s.wallet_teen_count ?? 0} teens`} testid="tile-wallets" />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-testid="nft-listings-table">
              <div className="px-4 py-3 border-b border-gray-100 font-bold text-gray-900 text-sm">Listings</div>
              {(data?.listings || []).length === 0 ? (
                <p className="p-6 text-sm text-gray-400">No listings yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                      <th className="px-4 py-2">NFT</th><th className="px-4 py-2">Teen</th>
                      <th className="px-4 py-2">Price</th><th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Listed</th><th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.listings.map(l => (
                      <tr key={l.listing_id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`listing-row-${l.listing_id}`}>
                        <td className="px-4 py-2.5 font-semibold text-gray-900">{l.title}</td>
                        <td className="px-4 py-2.5 text-gray-600">{l.teen_label}</td>
                        <td className="px-4 py-2.5 text-gray-900 font-semibold">
                          {l.status === 'sold' && l.sale_price_pence
                            ? `£${(l.sale_price_pence / 100).toFixed(2)}`
                            : l.price_pence ? `£${(l.price_pence / 100).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_CHIP[l.status] || STATUS_CHIP.cancelled}`}>
                            {(l.status || '').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{(l.listed_at || '').slice(0, 10)}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {l.status === 'listed' && (
                            <>
                              <button onClick={() => copyLink(l)} className="inline-flex items-center gap-1 px-2.5 py-1.5 mr-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100" data-testid={`copy-link-${l.listing_id}`}>
                                <Copy size={12} /> Link
                              </button>
                              <button onClick={() => markSold(l)} disabled={settling === l.listing_id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-500 disabled:opacity-50"
                                data-testid={`mark-sold-${l.listing_id}`}>
                                {settling === l.listing_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Mark Sold
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="mint-queue-panel">
                <div className="px-4 py-3 border-b border-gray-100 font-bold text-gray-900 text-sm">Mint Queue — waiting for parent</div>
                {(data?.mint_queue || []).length === 0 ? (
                  <p className="p-5 text-sm text-gray-400">Queue is clear.</p>
                ) : data.mint_queue.map(m => (
                  <div key={m.meme_id} className="px-4 py-2.5 border-b border-gray-50 flex justify-between text-sm">
                    <span className="font-semibold text-gray-800">{m.title}</span>
                    <span className="text-gray-500">{m.teen_label}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="pending-mints-panel">
                <div className="px-4 py-3 border-b border-gray-100 font-bold text-gray-900 text-sm">Confirming on-chain</div>
                {(data?.pending_mints || []).length === 0 ? (
                  <p className="p-5 text-sm text-gray-400">Nothing pending — finalizer worker is keeping this clear.</p>
                ) : data.pending_mints.map(m => (
                  <div key={m.meme_id} className="px-4 py-2.5 border-b border-gray-50 flex justify-between text-sm">
                    <span className="font-semibold text-gray-800">{m.title}</span>
                    <span className="text-gray-500">{m.teen_label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
